import type { CampaignState } from './CampaignButton';
import type { Size } from './types';
import CampaignButton from './CampaignButton';

/**
 * A single live sequence enrollment for a contact. `status` is the coarse enrollment lifecycle
 * (active / waiting / blocked / human / errored); the owner is who the sequence runs as. Field names match
 * the server's `sequence.forContact` rows, so hosts pass those rows straight through.
 */
export type ActiveSequenceEnrollment = {
  /** flow.sequence.public_id — the id passed back to `onUnenroll`. */
  sequencePublicId: string;
  name: string;
  status?: string;
  ownerUserId?: string | null;
  ownerName?: string | null;
  ownerProfilePicture?: string | null;
  /** When a rep paused this enrollment (null / absent = running). */
  pausedAt?: Date | string | null;
};

export type SequenceEnrollmentBadgeProps = {
  sequence: ActiveSequenceEnrollment;
  size?: Size;
  /** Remove the contact from this sequence. When omitted, the trash affordance is hidden (read-only). */
  onUnenroll?: (sequencePublicId: string) => void;
  isUnenrolling?: boolean;
  /**
   * Pause (`true`) or resume (`false`) this contact's enrollment — every arm holds where it is. When given,
   * the pill clicks like a campaign pill (pause ↔ resume); when omitted it's display-only.
   */
  onTogglePause?: (sequencePublicId: string, pause: boolean) => void;
  isTogglingPause?: boolean;
};

/** The enrollment lifecycle in the campaign pill's states, so a sequence reads in the same colours. */
const STATUS_TO_STATE: Record<string, CampaignState> = {
  active: 'active',
  waiting: 'active',
  // stuck, retrying on its own — the campaign pill's "needs a look" amber
  blocked: 'pending-review',
  // waiting on a person — the campaign pill's "needs you" purple
  human: 'action-required',
  errored: 'interrupted',
};

/** An errored enrollment has ended — there is nothing left to pause or resume. */
const UNPAUSABLE_STATUSES = new Set(['errored']);

/** The status as the pill's tooltip says it. */
const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  waiting: 'Waiting',
  blocked: 'Blocked',
  human: 'Needs review',
  errored: 'Errored',
  paused: 'Paused',
};

/**
 * The "in sequence: X" pill on the contact detail card — the campaign pill itself, so a sequence and a
 * campaign read alike: state colour + icon, the owner's avatar on the pill, the trash that unenrolls, and —
 * when the host wires `onTogglePause` — a click that pauses / resumes THIS contact's enrollment, like a
 * campaign pill. The owner is shown but never switched: who a sequence runs as is a property of the whole
 * sequence (changed in its builder, for every contact). Its presence is what
 * BLOCKS the enroll dropdown (see {@link CampaignButtonWithCadence}) — a contact already on a journey can't be
 * double-enrolled; you remove them here first.
 */
export default function SequenceEnrollmentBadge({
  sequence,
  size = 'sm',
  onUnenroll,
  isUnenrolling,
  onTogglePause,
  isTogglingPause,
}: SequenceEnrollmentBadgeProps) {
  const paused = sequence.pausedAt != null;
  const status = paused ? 'paused' : (sequence.status ?? 'active');
  const canTogglePause = onTogglePause != null && !UNPAUSABLE_STATUSES.has(sequence.status ?? 'active');
  return (
    <CampaignButton
      readOnly={!canTogglePause}
      noun="sequence"
      state={paused ? 'paused' : (STATUS_TO_STATE[status] ?? 'active')}
      name={sequence.name}
      size={size}
      tooltipOverride={`In sequence: ${sequence.name} · ${STATUS_LABEL[status] ?? status}${
        canTogglePause ? (paused ? ' — click to resume' : ' — click to pause') : ''
      }`}
      // A click while the last toggle is still in flight would send the same (stale) toggle again.
      onClick={
        canTogglePause
          ? () => {
              if (!isTogglingPause) onTogglePause(sequence.sequencePublicId, !paused);
            }
          : undefined
      }
      clickAction={canTogglePause ? (paused ? 'resume' : 'pause') : undefined}
      // The owner is set for the whole sequence (in its builder), so it's shown here but never switched.
      ownerFixed
      isPending={isTogglingPause}
      owner={
        sequence.ownerUserId != null
          ? {
              id: sequence.ownerUserId,
              name: sequence.ownerName ?? null,
              profilePicture: sequence.ownerProfilePicture ?? null,
            }
          : undefined
      }
      onDelete={onUnenroll != null ? () => onUnenroll(sequence.sequencePublicId) : undefined}
      isDeleting={isUnenrolling}
    />
  );
}
